/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import useClassNames from '@medflex/hooks/useClassNames';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import isSelf from '@medflex/utils/isSelf';
import isSupport from '@medflex/utils/isSupportPatient';
import PropTypes from 'prop-types';
import React from 'react';
import isTeamChatId from '@medflex/utils/isTeamChatId';
import { CheckIcon } from '@heroicons/react/24/outline';
import InputCheckBox from '../InputCheckbox';
import UserInfo from '../UserInfo';
import LastAtMessage from './LastAtMessage';

const CONTACT_LIST_ITEM = 'hover:bg-slate-100 cursor-pointer';
const ENABLED_CONTACT_LIST_ITEM =
  'text-slate-800 hover:text-blue-500 border-white hover:border-slate-100';
const DISABLED_CONTACT_LIST_ITEM = 'text-slate-800 border-white hover:border-slate-100';

const ContactListItem = ({
  avatarSrc,
  avatarInitials,
  title,
  subtitle,
  notificationCount,
  lastAtMessage,
  lastCheckedMessageId,
  enabled,
  isOnline,
  deactivated,
  selected,
  innerRef,
  className,
  showBorder,
  showFullBorder,
  avatarSize,
  ActionMenu,
  contactId,
  contactUserId,
  hasCheckBox,
  onClick,
  children,
  after,
  role,
  wrapperClassName,
  ...props
}) => {
  const isWideScreen = useIsWideScreen();

  const wrapperClassNames = useClassNames(
    () => [
      'flex',
      'flex-col',
      'justify-center',
      'select-none',
      showBorder &&
        (selected && !showFullBorder ? 'border-t border-b border-slate-200' : 'lg:border-r'),
      showFullBorder && 'border-r border-t border-b border-slate-200',
      wrapperClassName,
      className,
    ],
    [className, selected],
  );

  const contentClassNames = useClassNames(
    () => [
      'flex',
      'items-center',
      'text-left',
      'p-4',
      'border-l-4',
      'focus:outline-none',
      'focus:text-blue-600',
      'focus:font-semibold',
      'focus:bg-slate-100',
      CONTACT_LIST_ITEM,
      !selected && enabled && ENABLED_CONTACT_LIST_ITEM,
      !selected && !enabled && DISABLED_CONTACT_LIST_ITEM,
      ...(selected && enabled
        ? ['text-blue-600', 'font-semibold', 'cursor-auto', 'border-blue-600', 'bg-slate-100']
        : []),
      ...(selected && !enabled
        ? ['text-slate-400', 'cursor-default', 'font-semibold', 'bg-slate-100', 'border-slate-100']
        : []),
      className,
    ],
    [enabled, className, selected],
  );
  const [isHover, setIsHover] = React.useState(false);

  return (
    <li
      data-component="ContactListItem"
      className={wrapperClassNames}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onClick={onClick}
      {...props}
    >
      <div className={contentClassNames} ref={innerRef}>
        <UserInfo
          avatarSrc={avatarSrc}
          avatarInitials={avatarInitials}
          title={title}
          isOnline={isOnline}
          deactivated={deactivated}
          role={role}
          subtitle={subtitle}
          avatarSize={avatarSize}
          className={`flex-1${enabled ? '' : ' opacity-65'}`}
          titleCn={`line-clamp-2 ${notificationCount > 0 ? 'font-semibold' : ''}`}
        />
        {after && after}
        {hasCheckBox && (
          <InputCheckBox
            className="h-4 w-4 cursor-pointer"
            state={selected ? 'valid' : 'default'}
            checked={selected}
            readOnly
            tabIndex={-1}
          />
        )}
        <span className="flex flex-col ml-2 items-end">
          <div className="flex flex-row mb-2">
            {lastAtMessage && (
              <span className="text-sm font-normal text-slate-500">
                <LastAtMessage lastAt={lastAtMessage} />
              </span>
            )}
            <div
              className={`h-3 w-3 bg-red-600 rounded-full self-center ml-2 ${
                !notificationCount ? 'invisible' : ''
              }`}
            />
          </div>

          <div className="flex space-x-2 items-center h-6">
            {contactUserId &&
              !isSelf({ id: contactUserId }) &&
              !isTeamChatId(contactUserId) &&
              !isSupport({ id: contactUserId }) && (
                <>
                  {lastCheckedMessageId && (
                    <div className="ring-1 rounded bg-green-200 ring-green-400">
                      <CheckIcon className="w-6 h-6 text-green-800" />
                    </div>
                  )}
                  {ActionMenu && (
                    <ActionMenu
                      hidden={isWideScreen && !isHover}
                      onClickOutside={() => setIsHover(false)}
                      contactId={contactId}
                      enabled={enabled}
                    />
                  )}
                </>
              )}
          </div>
        </span>
      </div>
      {children && <div>{children}</div>}
    </li>
  );
};

ContactListItem.propTypes = {
  avatarSrc: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  avatarInitials: PropTypes.string,
  title: PropTypes.node,
  subtitle: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  showBorder: PropTypes.bool,
  isOnline: PropTypes.bool,
  deactivated: PropTypes.bool,
  showFullBorder: PropTypes.bool,
  hasCheckBox: PropTypes.bool,
  notificationCount: PropTypes.number,
  lastAtMessage: PropTypes.string,
  lastCheckedMessageId: PropTypes.string,
  avatarSize: PropTypes.oneOf(['6', '8', '10', '12', '16', '20', '24', '32']),
  enabled: PropTypes.bool,
  selected: PropTypes.bool,
  innerRef: PropTypes.shape({ current: PropTypes.any }),
  contactId: PropTypes.string,
  contactUserId: PropTypes.string,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  wrapperClassName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  children: PropTypes.node,
  ActionMenu: PropTypes.func,
  onClick: PropTypes.func,
  after: PropTypes.node,
  role: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

ContactListItem.defaultProps = {
  notificationCount: 0,
  avatarSize: '10',
  showBorder: false,
  isOnline: false,
  deactivated: false,
  showFullBorder: false,
  innerRef: null,
  onClick: () => {},
};

export default ContactListItem;
